import type { CustomerDocument, DealDocActivityEvent, DealDocActivityGroup } from '@protoss/shared';
import * as React from 'react';
import { salesApi } from '../services/salesApi';

interface UseDealDocumentsResult {
  documents: CustomerDocument[];
  isLoading: boolean;
  /** Per-document processing history, keyed by documentUuid (Docs tab badges + expandable detail). */
  activityByDoc: Record<string, DealDocActivityGroup>;
  /** Doc-less user events (question deletes/merges) - the "Manual edits" row. */
  userEvents: DealDocActivityEvent[];
  refetch: () => void;
}

export function useDealDocuments(dealUuid: string): UseDealDocumentsResult {
  const [documents, setDocuments] = React.useState<CustomerDocument[]>([]);
  const [activityByDoc, setActivityByDoc] = React.useState<Record<string, DealDocActivityGroup>>(
    {}
  );
  const [userEvents, setUserEvents] = React.useState<DealDocActivityEvent[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const refetch = React.useCallback(() => {
    if (!dealUuid) return;
    setIsLoading(true);
    // Activity is a decoration: its failure must never block the document list.
    Promise.all([
      salesApi.listDealDocuments(dealUuid),
      salesApi.getDealDocActivity(dealUuid).catch(() => null),
    ])
      .then(([{ documents: docs }, activity]) => {
        setDocuments(docs);
        if (activity) {
          const byDoc: Record<string, DealDocActivityGroup> = {};
          let docless: DealDocActivityEvent[] = [];
          for (const group of activity.groups) {
            if (group.documentUuid) byDoc[group.documentUuid] = group;
            else docless = group.events;
          }
          setActivityByDoc(byDoc);
          setUserEvents(docless);
        }
      })
      .catch((err) => console.error('[useDealDocuments] failed to load documents', err))
      .finally(() => setIsLoading(false));
  }, [dealUuid]);

  React.useEffect(() => {
    refetch();
  }, [refetch]);

  return { documents, isLoading, activityByDoc, userEvents, refetch };
}
